// Drawers (prototype drawerHTML): Dispatch, Disposition, Note, Data issue; and the hotkey help overlay.
// Phase 2: what is typed in Dispatch, Disposition and Note is a per-event draft (drafts.ts), so it is still
// there after switching to another event tab and back. Park and Send to live in Phase2Drawers.tsx.
// Phase 5: Talk-down, Deterrents and Notify live in DeterDrawers.tsx; the Disposition drawer links to Notify and says
// where the customer report goes.

import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { api, dispositionGuards, errorText, isConflict, type AgentDrafts, type DispatchDraft, type DispositionCode, type DispositionGuardDetails } from '../api.ts';
import { Icon, type IconName } from '../components/Icon.tsx';
import { useLoad } from '../hooks.ts';
import { fmtTime } from '../time.ts';
import type { Ctl, DataIssuePrefill } from './ctl.ts';
import { useDraft } from './drafts.ts';
import { addressLine, fmtPhone, human, str } from './util.ts';

/* ---------- Phase 4: the agent's drafts (api-phase4.md "Drafts") ---------- */

/**
 * The latest dispatch and report drafts (GET /api/events/:id/drafts), fetched when a drawer opens. Only where the
 * agent panel is visible: at a Level 0 site operators don't see the agent's work.
 */
function useAgentDrafts(c: Ctl): AgentDrafts | null {
  const p = c.agent.data;
  const visible = c.agent.on && p?.visible === true;
  const load = useLoad((sig) => (visible ? api.drafts(c.ev.id, sig) : Promise.resolve(null)), [c.ev.id, visible]);
  if (!visible) return null;
  return load.data ?? (p?.visible ? (p.drafts ?? null) : null);
}

/** The dispatch draft as text to read out: summary, subjects, vehicles, direction of travel. */
export function dispatchDraftText(d: DispatchDraft): string {
  const list = (xs: { description?: string; lastSeen?: string }[]) =>
    xs
      .filter((x) => x.description)
      .map((x, i) => `${xs.length > 1 ? `#${i + 1}: ` : ''}${x.description}${x.lastSeen ? ` (last seen ${x.lastSeen})` : ''}`)
      .join('. ');
  return [
    d.summary?.trim(),
    d.subjects.length ? `Subjects: ${list(d.subjects)}.` : null,
    d.vehicles.length ? `Vehicles: ${list(d.vehicles)}.` : null,
    d.direction?.trim() ? `Direction of travel: ${d.direction.trim()}.` : null,
  ]
    .filter(Boolean)
    .join('\n');
}

/* ---------- shell ---------- */

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Drawer({
  icon,
  title,
  sub,
  onClose,
  children,
  footer,
  onSubmit,
  conflict,
}: {
  icon: IconName;
  title: string;
  sub?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
  onSubmit: () => void;
  conflict?: boolean;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const id = useId();
  useEffect(() => {
    const prev = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const el = ref.current;
    const first = el?.querySelector<HTMLElement>('[data-autofocus]') ?? el?.querySelector<HTMLElement>('.db input, .db select, .db textarea, .db button');
    (first ?? el)?.focus();
    return () => prev?.focus();
  }, []);
  const trap = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Tab' || !ref.current) return;
    const items = Array.from(ref.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    const firstEl = items[0];
    const lastEl = items[items.length - 1];
    if (!firstEl || !lastEl) return;
    if (e.shiftKey && document.activeElement === firstEl) {
      e.preventDefault();
      lastEl.focus();
    } else if (!e.shiftKey && document.activeElement === lastEl) {
      e.preventDefault();
      firstEl.focus();
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit();
  };
  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="drawer" role="dialog" aria-modal="true" aria-labelledby={id} ref={ref} tabIndex={-1} onKeyDown={trap}>
        <form className="dform" onSubmit={submit} noValidate>
          <div className="dh">
            <Icon name={icon} />
            <div className="dh-t">
              <h3 id={id}>{title}</h3>
              {sub && <div className="note">{sub}</div>}
            </div>
            <button type="button" className="btn icon" onClick={onClose} aria-label="Close (Esc)" title="Close (Esc)">
              <Icon name="x" />
            </button>
          </div>
          <div className="db">
            {conflict && (
              <div className="warnbox" role="alert">
                <b>Event changed, review and retry</b>
                <div className="note fg">Someone or something updated this event. The latest version is loaded; check it, then submit again.</div>
              </div>
            )}
            {children}
          </div>
          <div className="df">{footer}</div>
        </form>
      </aside>
    </div>
  );
}

export function Err({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <div className="errbox" role="alert">
      <Icon name="alert" />
      <div className="mono">{typeof error === 'string' ? error : errorText(error)}</div>
      <span />
    </div>
  );
}

/* ---------- dispatch ---------- */

const CALL_TYPES = ['Burglary / theft in progress', 'Trespass in progress', 'Suspicious person(s)', 'Suspicious vehicle', 'Vandalism in progress', 'Fire or life safety', 'Other'];

export function DispatchDrawer({ c, onClose }: { c: Ctl; onClose: () => void }) {
  const ev = c.ev;
  const auth = ev.authority;
  const [agencyName, setAgency] = useDraft(c.drafts, ev.id, 'dispatch.agency', auth?.name ?? '');
  const [callType, setCallType] = useDraft(c.drafts, ev.id, 'dispatch.callType', CALL_TYPES[0] ?? '');
  const [desc, setDesc] = useDraft(c.drafts, ev.id, 'dispatch.desc', '');
  const [incident, setIncident] = useDraft(c.drafts, ev.id, 'dispatch.incident', '');
  const [note, setNote] = useDraft(c.drafts, ev.id, 'dispatch.note', '');
  const [tried, setTried] = useState(false);
  // Phase 4: the agent's dispatch draft pre-fills an empty description, once.
  const drafts = useAgentDrafts(c);
  const draft = drafts?.dispatch ?? null;
  const draftText = draft ? dispatchDraftText(draft) : '';
  const [fromAi, setFromAi] = useDraft(c.drafts, ev.id, 'dispatch.fromAi', false);
  useEffect(() => {
    if (draftText && !fromAi && !desc.trim()) {
      setDesc(draftText);
      setFromAi(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftText]);
  const prior = ev.actions.filter((a) => a.type === 'dispatch');
  const valid = agencyName.trim() && callType && desc.trim();
  const submit = async () => {
    setTried(true);
    if (!valid) return;
    const r = await c.run(
      'Dispatch',
      (v) =>
        api.dispatch(
          ev.id,
          {
            authorityId: auth && agencyName.trim() === auth.name ? auth.id : null,
            agencyName: agencyName.trim(),
            agencyIncidentNumber: incident.trim() || null,
            callType,
            subjectDescription: desc.trim(),
            ...(note.trim() ? { note: note.trim() } : {}),
          },
          v,
        ),
      (d) => d,
    );
    if (r) {
      // The dispatch is on the timeline and in the log.
      onClose();
      c.drafts.clear(ev.id, 'dispatch.');
    }
  };
  return (
    <Drawer
      icon="shield"
      title="Dispatch police"
      conflict={c.conflict}
      sub={auth ? `${auth.name} · matched for this site` : 'No authority matched: enter the agency'}
      onClose={onClose}
      onSubmit={() => void submit()}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn pri" disabled={!!c.busy}>
            <Icon name="check" />
            Mark dispatched
          </button>
        </>
      }
    >
      {auth && (
        <div className="contact">
          <span className="o">
            <Icon name="shield" />
          </span>
          <div className="cmain">
            <div className="nm">{auth.name}</div>
            <div className="ph big">{fmtPhone(auth.nonEmergency)}</div>
            <div className="muted small">Non-emergency · dial manually{auth.note ? ` · ${auth.note}` : ''}</div>
          </div>
          <span />
        </div>
      )}
      {prior.length > 0 && (
        <div className="banner">
          <Icon name="log" />
          <span>
            Already dispatched {prior.length}×:{' '}
            {prior.map((p) => `${str(p.params.agencyName) ?? 'agency'}${str(p.params.agencyIncidentNumber) ? ` #${str(p.params.agencyIncidentNumber)}` : ''}`).join('; ')}. Log again to add an
            incident number.
          </span>
        </div>
      )}
      <div className="row2">
        <div className="field">
          <span className="lbl">Address</span>
          <div>{addressLine(ev) || '—'}</div>
        </div>
        <div className="field">
          <span className="lbl">Cross streets</span>
          <div>{ev.site.crossStreets ?? '—'}</div>
        </div>
      </div>
      <label className="field">
        <span className="lbl">Agency</span>
        <input value={agencyName} onChange={(e) => setAgency(e.target.value)} aria-invalid={tried && !agencyName.trim()} data-autofocus={auth ? undefined : true} />
      </label>
      <label className="field">
        <span className="lbl">Call type</span>
        <select value={callType} onChange={(e) => setCallType(e.target.value)} data-autofocus={auth ? true : undefined}>
          {CALL_TYPES.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
      </label>
      <label className={`field${fromAi && draft ? ' aidraft' : ''}`}>
        {fromAi && draft ? (
          <span className="lbl ai-t">
            <Icon name="spark" /> AI draft — check before you use it · subject description{draft.createdAt ? ` · ${fmtTime(draft.createdAt, ev.siteTimeZone)}` : ''}
          </span>
        ) : (
          <span className="lbl">Subject description · read out to the dispatcher</span>
        )}
        <textarea rows={6} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="How many, clothing, what they carry, where they are now, vehicles" aria-invalid={tried && !desc.trim()} />
        {tried && !desc.trim() && <span className="inline-err">Describe the subjects for the police.</span>}
        {fromAi && draft && <span className="note">Drafted by the AI from what it saw. Edit it to match what you see; nothing is sent until you call.</span>}
      </label>
      {draft && desc !== draftText && (
        <button
          type="button"
          className="btn sm aib self-start"
          onClick={() => {
            setDesc(desc.trim() ? `${desc.trim()}\n${draftText}` : draftText);
            setFromAi(true);
          }}
          title={draftText}
        >
          <Icon name="spark" />
          Insert the AI draft
        </button>
      )}
      <label className="field">
        <span className="lbl">Agency incident number</span>
        <input value={incident} onChange={(e) => setIncident(e.target.value)} placeholder="e.g. MPD-26-104512" className="mono" />
        <span className="note">Some dispositions need it. You can log it later by dispatching again.</span>
      </label>
      <label className="field">
        <span className="lbl">Note (optional)</span>
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <div className="field">
        <span className="lbl">Officer access</span>
        <div className="note">Never disclose gate or alarm codes on the phone. Follow the special instructions.</div>
      </div>
    </Drawer>
  );
}

/* ---------- disposition ---------- */

function groupCodes(codes: DispositionCode[]): [string, DispositionCode[]][] {
  const m = new Map<string, DispositionCode[]>();
  for (const d of codes.filter((x) => x.active).sort((a, b) => a.sortOrder - b.sortOrder)) {
    const l = m.get(d.category) ?? [];
    l.push(d);
    m.set(d.category, l);
  }
  return [...m.entries()];
}

export function DispositionDrawer({ c, onClose }: { c: Ctl; onClose: () => void }) {
  const ev = c.ev;
  const [code, setCode] = useDraft<string | null>(c.drafts, ev.id, 'dispo.code', null);
  const [notes, setNotes] = useDraft(c.drafts, ev.id, 'dispo.notes', '');
  const [report, setReport] = useDraft(c.drafts, ev.id, 'dispo.report', false);
  const [fields, setFields] = useDraft<Record<string, string>>(c.drafts, ev.id, 'dispo.fields', {});
  const [guards, setGuards] = useState<DispositionGuardDetails | null>(null);
  const reportDraft = useAgentDrafts(c)?.report ?? null;
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const groups = groupCodes(ev.dispositionCodes);
  const sel = ev.dispositionCodes.find((d) => d.code === code) ?? null;
  const pendingRequired = (ev.mission?.steps ?? []).filter((s) => s.required && s.status === 'pending');
  const hasPoliceNo = ev.actions.some((a) => a.type === 'dispatch' && a.status !== 'failed' && !!str(a.params.agencyIncidentNumber));
  const stepTitle = (k: string) => ev.mission?.steps.find((s) => s.id === k || String(s.seq) === k)?.title ?? k;
  const needNotes = !!sel?.requiresNotes && !notes.trim();
  const missingFieldNames = guards?.missingFields ?? [];

  const pick = (d: DispositionCode) => {
    setCode(d.code);
    setReport(d.notifiesCustomer);
    setGuards(null);
    setErr(null);
  };
  const submit = async () => {
    if (!sel || needNotes) return;
    setBusy(true);
    setErr(null);
    const filled = Object.fromEntries(Object.entries(fields).filter(([, v]) => v.trim()));
    const r = await c.run(
      'Disposition',
      (v) => api.disposition(ev.id, { code: sel.code, notes: notes.trim() || undefined, customerReport: report, ...(Object.keys(filled).length ? { fields: filled } : {}) }, v),
      (d) => d,
      (e) => {
        const g = dispositionGuards(e);
        if (g) {
          setGuards(g);
          return true;
        }
        // 409: let run() refetch the event and show "Event changed, review and retry".
        if (isConflict(e)) return false;
        setErr(e);
        return true;
      },
    );
    setBusy(false);
    if (r) {
      onClose();
      c.afterClose(r);
    }
  };

  return (
    <Drawer
      icon="check"
      title="Disposition"
      conflict={c.conflict}
      sub={`${ev.site.name} · ${ev.displayNo}`}
      onClose={onClose}
      onSubmit={() => void submit()}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn pri" disabled={!sel || needNotes || busy}>
            <Icon name="check" />
            {busy ? 'Closing…' : 'Close event'}
          </button>
        </>
      }
    >
      {(pendingRequired.length > 0 || guards) && (
        <div className="warnbox" role={guards ? 'alert' : undefined}>
          {guards ? (
            <>
              <b>The event cannot close yet</b>
              {guards.missingSteps.length > 0 && (
                <div className="note fg">
                  Required steps not done: <br />
                  {guards.missingSteps.map((k) => (
                    <span key={k}>
                      · {stepTitle(k)}
                      <br />
                    </span>
                  ))}
                </div>
              )}
              {missingFieldNames.length > 0 && <div className="note fg">Missing: {missingFieldNames.map((f) => f.replace(/_/g, ' ')).join(', ')}</div>}
              {guards.pendingApprovals.length > 0 && (
                <div className="note fg">
                  The agent is waiting for your answer: {guards.pendingApprovals.join('; ')}. Approve or deny it (Agent tab, Y / N), or let it expire.
                </div>
              )}
              <div className="note">Finish them on the Mission tab (or mark N/A), or pick a code that does not need them.</div>
            </>
          ) : (
            <>
              <b>
                {pendingRequired.length} required mission step{pendingRequired.length > 1 ? 's' : ''} not done
              </b>
              <div className="note fg">
                {pendingRequired.map((s) => (
                  <span key={s.id}>
                    · {s.title}
                    <br />
                  </span>
                ))}
              </div>
              <div className="note">Codes that need required steps will be refused until these are done or N/A.</div>
            </>
          )}
        </div>
      )}
      {groups.length === 0 && <div className="note">No active disposition codes. An admin adds them in Setup.</div>}
      <div role="radiogroup" aria-label="Disposition code" className="dgroups">
        {groups.map(([cat, list]) => (
          <div key={cat} className="sec">
            <div className="lbl">{human(cat)}</div>
            {list.map((d) => {
              const reqs = [d.requiresPoliceNumber && 'police #', d.requiresNotes && 'notes', d.requiresRequiredSteps && 'required steps'].filter(Boolean).join(' · ');
              return (
                <button key={d.code} type="button" role="radio" aria-checked={code === d.code} className={`opt${code === d.code ? ' on' : ''}`} onClick={() => pick(d)}>
                  <span className="rd" aria-hidden="true" />
                  <span>
                    {d.label}
                    {reqs && <small>Needs {reqs}</small>}
                  </span>
                  <span className="mono dim tiny">{d.code}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
      {sel?.requiresPoliceNumber && !hasPoliceNo && (
        <div className="warnbox">
          <b>Needs a police incident number</b>
          <div className="note fg">Log the dispatch with the agency incident number first.</div>
          <button type="button" className="btn sm" onClick={() => c.openDrawer({ k: 'dispatch' })}>
            <Icon name="shield" />
            Open dispatch
          </button>
        </div>
      )}
      {missingFieldNames.map((f) => (
        <label key={f} className="field">
          <span className="lbl">{f.replace(/_/g, ' ')}</span>
          <input value={fields[f] ?? ''} onChange={(e) => setFields((m) => ({ ...m, [f]: e.target.value }))} />
        </label>
      ))}
      <label className="field">
        <span className="lbl">Notes{sel?.requiresNotes ? ' · required for this code' : ''}{report ? ' · go in the customer report' : ''}</span>
        <textarea rows={report ? 5 : 3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What happened, in two or three sentences" aria-invalid={needNotes} />
      </label>
      {reportDraft && !notes.includes(reportDraft.narrative.trim()) && (
        <div className="aidraft">
          <span className="lbl ai-t">
            <Icon name="spark" /> AI draft — check before you use it · customer report{reportDraft.createdAt ? ` · ${fmtTime(reportDraft.createdAt, ev.siteTimeZone)}` : ''}
          </span>
          <span className="small">{reportDraft.narrative}</span>
          <button
            type="button"
            className="btn sm aib self-start"
            onClick={() => {
              setNotes((n) => (n.trim() ? `${n.trim()}\n\n${reportDraft.narrative.trim()}` : reportDraft.narrative.trim()));
              setReport(true);
            }}
          >
            <Icon name="spark" />
            Insert into the notes
          </button>
        </div>
      )}
      <button type="button" className={`opt${report ? ' on' : ''}`} role="checkbox" aria-checked={report} onClick={() => setReport((r) => !r)}>
        <span className="cb" aria-hidden="true">
          {report && <Icon name="check" />}
        </span>
        <span>
          Send customer incident report
          <small>
            {sel ? (sel.notifiesCustomer ? 'This code notifies the customer by default' : 'This code does not notify the customer by default') : 'Pick a code first'}
            {c.features.deterrence && report ? ". Emailed to the site's customer-report contacts after you close; with none set up it stays with the event." : ''}
          </small>
        </span>
        <span />
      </button>
      {c.features.deterrence && (
        <div className="row-wrap">
          <button type="button" className="btn sm" onClick={() => c.openDrawer({ k: 'notify' })}>
            <Icon name="send" />
            Notify the customer now
          </button>
          <span className="note">A text or email (activity, police, all clear) before you close.</span>
        </div>
      )}
      <Err error={err} />
    </Drawer>
  );
}

/* ---------- note ---------- */

export function NoteDrawer({ c, onClose }: { c: Ctl; onClose: () => void }) {
  const [text, setText] = useDraft(c.drafts, c.ev.id, 'note.text', '');
  const submit = async () => {
    if (!text.trim()) return;
    const r = await c.run('Note', (v) => api.addNote(c.ev.id, text.trim(), v), (d) => d);
    if (r) {
      onClose();
      c.drafts.clear(r.id, 'note.');
    }
  };
  return (
    <Drawer
      icon="note"
      title="Add a note"
      conflict={c.conflict}
      sub={`${c.ev.site.name} · ${c.ev.displayNo}`}
      onClose={onClose}
      onSubmit={() => void submit()}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn pri" disabled={!text.trim() || !!c.busy}>
            <Icon name="check" />
            Add note
          </button>
        </>
      }
    >
      <label className="field">
        <span className="lbl">Note</span>
        <textarea
          rows={6}
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-autofocus
          placeholder="What you saw or did. Notes are part of the event record."
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              void submit();
            }
          }}
        />
        <span className="note">Ctrl+Enter to save.</span>
      </label>
    </Drawer>
  );
}

/* ---------- data issue ---------- */

// Enums from contracts/schemas/data_issue_reported.json (the API doc does not list them).
const ISSUE_TYPES = [
  ['phone_wrong_person', 'Wrong number (reaches someone else)'],
  ['phone_disconnected', 'Number disconnected'],
  ['contact_left_company', 'Contact left the company'],
  ['wrong_address', 'Wrong address'],
  ['wrong_geo', 'Wrong map location'],
  ['wrong_time_zone', 'Wrong time zone'],
  ['wrong_hours', 'Wrong contracted hours'],
  ['camera_mislabeled', 'Camera mislabeled'],
  ['map_placement_wrong', 'Map placement wrong'],
  ['duplicate_binding', 'Duplicate analytics binding'],
  ['missing_field', 'Missing information'],
  ['reference_invalid', 'Broken reference'],
  ['other', 'Other'],
] as const;
const SEVERITIES = [
  ['degrades_response', 'Degrades response'],
  ['blocks_monitoring', 'Blocks monitoring'],
  ['cosmetic', 'Cosmetic'],
] as const;
const ENTITY_TYPES = [
  ['contact', 'Contact'],
  ['site', 'Site'],
  ['camera', 'Camera'],
  ['sitemap', 'Site map'],
  ['company', 'Company'],
] as const;

export function DataIssueDrawer({ c, prefill, onClose }: { c: Ctl; prefill: DataIssuePrefill; onClose: () => void }) {
  const ev = c.ev;
  const contacts = [...new Map(ev.callProcedure.map((s) => [s.contactId, s.contactName])).entries()];
  const [entityType, setEntityType] = useState(prefill.entityType ?? 'contact');
  const [entityId, setEntityId] = useState(prefill.entityId ?? '');
  const [phoneId, setPhoneId] = useState(prefill.subItem?.kind === 'phone' ? prefill.subItem.id : '');
  const [field, setField] = useState(prefill.field ?? '');
  const [issueType, setIssueType] = useState(prefill.issueType ?? 'phone_wrong_person');
  const [severity, setSeverity] = useState(prefill.severity ?? 'degrades_response');
  const [description, setDescription] = useState(prefill.description ?? '');
  const [suggested, setSuggested] = useState(prefill.suggestedValue ?? '');
  const [tried, setTried] = useState(false);

  const entities: [string, string][] =
    entityType === 'contact'
      ? contacts
      : entityType === 'camera'
        ? ev.cameras.map((x) => [x.id, x.name])
        : entityType === 'sitemap'
          ? ev.sitemaps.map((m) => [m.id, m.name])
          : entityType === 'company'
            ? [[ev.site.companyId, ev.site.companyName]]
            : [[ev.site.id, ev.site.name]];
  const phones = entityType === 'contact' ? ev.callProcedure.filter((s) => s.contactId === entityId) : [];
  const effectiveId = entityId || entities[0]?.[0] || '';
  const valid = effectiveId && description.trim();

  const submit = async () => {
    setTried(true);
    if (!valid) return;
    const r = await c.run(
      'Data issue',
      (v) =>
        api.dataIssue(
          ev.id,
          {
            entityType,
            entityId: effectiveId,
            ...(phoneId && entityType === 'contact' ? { subItem: { kind: 'phone' as const, id: phoneId } } : {}),
            ...(field.trim() ? { field: field.trim() } : {}),
            issueType,
            severity,
            description: description.trim(),
            ...(suggested.trim() ? { suggestedValue: suggested.trim() } : {}),
          },
          v,
        ),
      (d) => d,
    );
    if (r) {
      onClose();
    }
  };

  return (
    <Drawer
      icon="alert"
      title="Report a data issue"
      conflict={c.conflict}
      sub="ERP-owned data is fixed in the ERP. This sends it to the owner."
      onClose={onClose}
      onSubmit={() => void submit()}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn pri" disabled={!!c.busy}>
            <Icon name="send" />
            Report
          </button>
        </>
      }
    >
      <div className="row2">
        <label className="field">
          <span className="lbl">Entity</span>
          <select
            value={entityType}
            onChange={(e) => {
              setEntityType(e.target.value);
              setEntityId('');
              setPhoneId('');
            }}
          >
            {ENTITY_TYPES.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="lbl">Which</span>
          <select value={effectiveId} onChange={(e) => setEntityId(e.target.value)} aria-invalid={tried && !effectiveId}>
            {entities.length === 0 && <option value="">None on this event</option>}
            {entities.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {entityType === 'contact' && phones.length > 0 && (
        <label className="field">
          <span className="lbl">Phone</span>
          <select value={phoneId} onChange={(e) => setPhoneId(e.target.value)}>
            <option value="">Not about one phone</option>
            {phones.map((p) => (
              <option key={p.phoneId} value={p.phoneId}>
                {p.phoneType ?? 'phone'} · {fmtPhone(p.e164, p.extension)}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="row2">
        <label className="field">
          <span className="lbl">Issue type</span>
          <select value={issueType} onChange={(e) => setIssueType(e.target.value)}>
            {ISSUE_TYPES.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="lbl">Severity</span>
          <select value={severity} onChange={(e) => setSeverity(e.target.value)}>
            {SEVERITIES.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="field">
        <span className="lbl">Field (optional)</span>
        <input value={field} onChange={(e) => setField(e.target.value)} placeholder="e.g. e164, jobTitle, address" className="mono" />
      </label>
      <label className="field">
        <span className="lbl">Description</span>
        <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} data-autofocus aria-invalid={tried && !description.trim()} />
        {tried && !description.trim() && <span className="inline-err">Say what is wrong.</span>}
      </label>
      <label className="field">
        <span className="lbl">Suggested value (optional)</span>
        <input value={suggested} onChange={(e) => setSuggested(e.target.value)} placeholder="The right number or text, if you know it" />
      </label>
    </Drawer>
  );
}

/* ---------- help ---------- */

const KEYS: [string, string][] = [
  ['G', 'Agent panel · tell the agent'],
  ['Y / N', 'Approve / deny the agent’s request'],
  ['1–9', 'Camera by distance (numbers on lanes and the matrix)'],
  ['Space', 'Play / pause'],
  ['A', 'Alarm clip'],
  ['L', 'Live'],
  ['S', 'Sync all to alarm'],
  ['Q', 'Quad (alarm cameras) / single'],
  ['M', 'Camera matrix (own window)'],
  ['T (hold)', 'Push-to-talk on the chosen speakers · tap: Talk-down'],
  ['R', 'Deterrents (two-step confirm)'],
  ['J', 'Next unreviewed alarm'],
  ['D', 'Dispatch'],
  ['K', 'Call list'],
  ['O', 'Note'],
  ['B', 'Bookmark this moment'],
  ['P', 'Park'],
  ['X', 'Send to…'],
  ['C', 'Disposition'],
  ['[  ]', 'Previous / next event'],
  ['Enter', 'Accept the offer'],
  ['Esc', 'Close panel · decline the offer'],
  ['?', 'This help'],
];

export function HelpOverlay({ onClose, deterrence = false }: { onClose: () => void; deterrence?: boolean }) {
  const keys = deterrence ? KEYS : KEYS.filter(([k]) => k !== 'T (hold)' && k !== 'R');
  const ref = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const prev = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    ref.current?.focus();
    return () => prev?.focus();
  }, []);
  return (
    <div className="help" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card" role="dialog" aria-modal="true" aria-labelledby="help-h">
        <div className="row-wrap">
          <h3 id="help-h" className="sp help-h">
            Hotkeys
          </h3>
          <button ref={ref} type="button" className="btn icon" onClick={onClose} aria-label="Close help">
            <Icon name="x" />
          </button>
        </div>
        <div className="keys">
          {keys.map(([k, l]) => (
            <div key={k}>
              <span>{l}</span>
              <kbd>{k}</kbd>
            </div>
          ))}
        </div>
        <div className="lbl">How an event goes</div>
        <ol className="tour">
          <li>Set yourself Available (top bar). In push mode events are offered to you: Enter accepts, Esc declines. In pull mode, click a queue card to claim it.</li>
          <li>You can hold several events up to your cap; switch between their tabs with [ and ]. Each tab keeps its own video and drafts.</li>
          <li>The timeline has a lane per camera: click an alarm marker to load its clip (hover it to split or dismiss); press J for the next unreviewed one. S syncs the alarm cameras in a quad; M opens every site camera in its own window, synced to your player.</li>
          <li>Follow the Mission tab. Steps check off from what you do: video review, calls, dispatch, notes.</li>
          <li>Call contacts in order from the Contacts tab (K). Dial the number shown, then record the outcome.</li>
          {deterrence && (
            <li>
              Deter: hold T to talk on the chosen speakers (a tap opens Talk-down, with the library and typed messages); R opens the deterrents: press, then confirm. If the AI already played a talk-down or
              pulsed a strobe, it is on the timeline's AI row.
            </li>
          )}
          <li>Dispatch (D) logs the police call with the agency incident number.</li>
          <li>Waiting on police or a callback? Park it (P) to free the slot. It comes back to you on its timer or when a new alarm arrives.</li>
          <li>Send to (X) hands the event to another operator or a group with a note.</li>
          <li>Close with a disposition (C). You go back to the queue.</li>
        </ol>
        <div className="note">Hotkeys are ignored while you type in a field.</div>
      </div>
    </div>
  );
}
